import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AuthenticatedAvatarImage } from "@/components/AuthenticatedAvatarImage";
import { cn, resolveEatzyMediaUrl } from "@/lib/utils";
import TrustBadge, { getTrustTier } from "@/components/TrustBadge";

interface UserAvatarProps {
  src?: string;
  name: string;
  size?: "sm" | "md" | "lg" | "xl";
  showOnline?: boolean;
  className?: string;
  verified?: boolean;
  trustScore?: number; // 0-100 numeric score
  showTrustBadge?: boolean;
}

export default function UserAvatar({ 
  src, 
  name, 
  size = "md", 
  showOnline = false, 
  className,
  verified = false,
  trustScore = 75,
  showTrustBadge = false
}: UserAvatarProps) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
    xl: "w-16 h-16"
  };
  
  const onlineDotSizes = {
    sm: "w-2 h-2",
    md: "w-2.5 h-2.5", 
    lg: "w-3 h-3",
    xl: "w-4 h-4"
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const trustTier = getTrustTier(trustScore);
  
  // Map avatar size to trust badge size
  const trustBadgeSizeMap = {
    sm: 'xs' as const,
    md: 'xs' as const,
    lg: 'sm' as const,
    xl: 'sm' as const,
  };

  return (
    <div className={cn("relative inline-block", className)} data-testid="user-avatar">
      <Avatar className={cn(sizeClasses[size], "ring-2 ring-background")}>
        <AuthenticatedAvatarImage src={resolveEatzyMediaUrl(src)} alt={name} />
        <AvatarFallback className="bg-primary/10 text-primary font-medium">
          {getInitials(name)}
        </AvatarFallback>
      </Avatar>
      
      {showOnline && (
        <div 
          className={cn(
            "absolute -bottom-0.5 -right-0.5 rounded-full bg-status-online ring-2 ring-background",
            onlineDotSizes[size]
          )}
          data-testid="online-indicator"
        />
      )}
      
      {verified && (
        <div 
          className={cn(
            "absolute -top-0.5 -right-0.5 bg-primary rounded-full ring-2 ring-background flex items-center justify-center text-white text-xs font-bold",
            size === "sm" ? "w-4 h-4" : "w-5 h-5"
          )}
          data-testid="verified-badge"
        >
          ✓
        </div>
      )}
      
      {showTrustBadge && (
        <div 
          className="absolute -bottom-1 -right-1"
          data-testid="avatar-trust-badge"
        >
          <TrustBadge
            trustScore={trustScore}
            trustTier={trustTier}
            size={trustBadgeSizeMap[size]}
            showScore={false}
          />
        </div>
      )}
    </div>
  );
}